import { ReactElement, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Component as PlusIcon } from 'icons/icon-ui--plus.svg';
import { Component as ClockIcon } from 'icons/icon-ui--clock.svg';
import { Component as BoxIcon } from 'icons/icon-box.svg';
import { parseZone } from 'moment';
import { useNavigateWithUTMs } from '../../hooks/useNavigateWithUTMs';
import { BreakpointMedia } from '../../utils/config';
import styles from './LimitedTimeOffering.module.scss';
import { NutritionLabelModal } from '../shared/modal/NutritionLabelModal';
import { LtoComponent, LtoItem } from '../../models/product.types';
import { Button } from '../shared/button/Button';
import { MenuCartItem } from '../../models/menu.types';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { useAuth } from '../../contexts/AuthContext';
import RecipeImages from './RecipeImages';
import { QuantitySelector } from '../shared/quantity-selector/QuantitySelector';
import { FormattedText } from './FormattedText';
import { getFormattedDeliveryDateRangeText } from '../../utils/dateTimes';
import { LAST_LTO_WEEK_MENU_ID } from '../../constants/menu';
import { useUser } from '../../contexts/UserContext';
import { useNotification } from '../../contexts/NotificationContext';
import { SubscriptionStatus } from '../../models/user.types';
import { Toast, ToastVariant } from '../shared/notification/Toast';

type Props = {
  onUpdateCartQuantity: (cartItem: MenuCartItem, quantity: number) => void;
  ltoMenuItem?: MenuCartItem
  currentLtoComponent?: LtoComponent;
  currentLTORecipe: number;
  setCurrentLTORecipe?: ((index: any) => void) | null;
  totalNumberOfRecipes: number
  ltoItem?: LtoItem;
}

function LimitedTimeOfferingCurrentRecipeInfo({
  onUpdateCartQuantity,
  ltoMenuItem,
  currentLtoComponent,
  currentLTORecipe,
  setCurrentLTORecipe,
  ltoItem,
  totalNumberOfRecipes,
}: Props): ReactElement {
  const { isLoggedIn } = useAuth();
  const [isNutritionLabelOpen, setIsNutritionLabelOpen] = useState(false);
  const stackElements = !useMediaQuery({ query: BreakpointMedia.MIN_LTO });
  const isNotMobileWidth = !useMediaQuery({ query: BreakpointMedia.MAX_SM });
  const {
    addToCart,
    menu,
    onSelectMenu,
    cart,
    reactivateSubscription,
    setIsCartDrawerOpen,
  } = useMenuWithCart();
  const menuSelectorDate = menu?.deliveryOn ? parseZone(menu?.deliveryOn)
    .format('ddd, MMM D, YYYY') : '';
  const weekOfDate = menu?.deliveryOn ? parseZone(menu?.deliveryOn)
    .format('MMMM DD') : '';
  const {
    sku,
    product,
    itemNumber,
    title,
    prepAndCookTimeText,
    fiber,
    description,
    tip,
  } = currentLtoComponent ?? {};
  const nutrition = {
    Cal: Math.round(sku?.calories ?? 0),
    Fiber: `${fiber}g`,
    Carbs: `${Math.round(Number(sku?.carbs ?? 0))}g`,
    Protein: `${Math.round(Number(sku?.protein ?? 0))}g`,
  };
  const { navigate } = useNavigateWithUTMs();
  const hyperlinkUrl = 'https://www.robinarzon.com/eat-to-hustle-1';
  const breadcrumbUrl = isLoggedIn ? '/upcoming-menus' : '/shop';
  const breadcrumbText = isLoggedIn ? `${weekOfDate} Menu Week` : 'Shop the Menu';
  const handleLoggedInBreadcrumbClick = () => {
    if (isLoggedIn && menu && cart) {
      onSelectMenu(menu?.id, cart?.id);
    }
    navigate(breadcrumbUrl);
  };
  const isLtoAvailable = () => {
    const isLastLtoWeek = menu?.id === LAST_LTO_WEEK_MENU_ID;
    if (isLastLtoWeek && !ltoMenuItem?.isInStock) {
      return false;
    }
    return isLoggedIn ? ltoMenuItem?.isInStock : !!ltoMenuItem;
  };

  const { user } = useUser();
  const { showNotification } = useNotification();
  const isInactiveSubscription = user?.status
    && (user.status === SubscriptionStatus.RETURNING
      || user.status === SubscriptionStatus.CANCELLED);
  const handleAddToCart = async () => {
    if (isInactiveSubscription) {
      await reactivateSubscription();
      showNotification({
        content: <Toast variant={ToastVariant.ACCOUNT_ACTIVE} />,
        duration: 5000,
      });
    }
    if (ltoMenuItem?.isInStock) {
      addToCart(ltoMenuItem);
    } else if (ltoMenuItem) {
      addToCart(ltoMenuItem, true);
      if (isInactiveSubscription) {
        setIsCartDrawerOpen(true);
      }
    }
  };

  const quantity = ltoMenuItem?.quantity ?? 0;

  const cartActionButton = () => {
    if (quantity > 0) {
      if (isInactiveSubscription) {
        return <></>;
      }
      return (
        <QuantitySelector
          quantity={quantity}
          onAddQuantity={() => ltoMenuItem && onUpdateCartQuantity(ltoMenuItem, quantity + 1)}
          onSubtractQuantity={() => ltoMenuItem && onUpdateCartQuantity(ltoMenuItem, quantity - 1)}
        />
      );
    }

    let buttonText = isNotMobileWidth ? 'Add To Cart & Sign Up' : 'Add To Cart';

    if (isLoggedIn) {
      buttonText = isInactiveSubscription ? 'Re-Activate Account & Reserve Your Box' : 'Add To Cart';
    }

    return (
      <Button
        text={buttonText}
        icon={PlusIcon}
        onClick={handleAddToCart}
        isEnabled
        className={styles.button_styling}
        style={{ width: !isNotMobileWidth ? '360px' : '' }}
      />
    );
  };
  return (
    <div className={styles.recipe_row}>
      <RecipeImages
        setCurrentRecipe={setCurrentLTORecipe}
        currentRecipe={currentLTORecipe}
        ltoItem={ltoItem}
        totalNumberOfRecipes={totalNumberOfRecipes}
        handleLoggedInBreadcrumbClick={handleLoggedInBreadcrumbClick}
        breadcrumbText={breadcrumbText}
      />
      <div className={styles.recipe_info}>
        {!stackElements && (
          <div className={styles.breadcrumbs}>
            <a
              className={styles.week_of}
              role="button"
              onClick={handleLoggedInBreadcrumbClick}
            >
              {breadcrumbText}
            </a>
            <span className={styles.separator}>/</span>
            Eat To Hustle
          </div>
        )}
        <h1 className={styles.title}>Eat To Hustle x Purple Carrot</h1>
        {isNotMobileWidth && (
          <div
            className={`${styles.lto_price_row} ${
              isInactiveSubscription ? styles.vertical_align : ''
            }`}
          >
            {!!ltoMenuItem && (
              isLtoAvailable()
                ? cartActionButton()
                : (
                  <Button
                    text="Sold Out"
                    onClick={() => console.log('Sold Out')}
                    isEnabled={false}
                  />
                )
            )}
            <div>
              <span className={styles.lto_price}>$125</span>
              <span className={styles.meta}>
                {' '}
                /
                {'  '}
                <strong>4</strong>
                {'  '}
                dinner meal kits / serves 2–4
              </span>
            </div>
          </div>
        )}
        <div className={styles.delivery_row}>
          {isNotMobileWidth ? (
            <div className={styles.delivery_date}>
              <BoxIcon width="24" />
              <strong>
                {isLoggedIn ? menuSelectorDate
                  : getFormattedDeliveryDateRangeText(menu?.deliveryOn || '', false)}
              </strong>
              Delivery
            </div>
          ) : (
            <div>
              <span className={styles.lto_price}>$125</span>
              <span className={styles.meta}>
                {' '}
                /
                {'  '}
                <strong>4</strong>
                {'  '}
                dinner meal kits / serves 2–4
              </span>
            </div>
          )}
          <div className={styles.cook_time}>
            <ClockIcon />
            {' '}
            {prepAndCookTimeText}
          </div>
        </div>
        {sku && (
          <div className={styles.nutrition_row}>
            {Object.entries(nutrition)
              .map(
                ([key, value], idx) => (
                  <span key={key} className={styles.nutrition_item}>
                    {idx > 0 && <span className={styles.separator}>•</span>}
                    <span>
                      <span className={styles.nutrition_value}>{value}</span>
                      {' '}
                      <span className={styles.nutrition_label}>{key}</span>
                    </span>
                  </span>
                ),
              )}
            <span className={styles.separator}>•</span>
            <div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setIsNutritionLabelOpen(true)}
                className="c-recipe__link c-recipe__link--nutrition"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setIsNutritionLabelOpen(true);
                  }
                }}
                style={{
                  color: '#297B03',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                View Full Nutrition Label
              </div>
            </div>
            {sku?.nutrition_label && (
              <NutritionLabelModal
                isOpen={isNutritionLabelOpen}
                title={title || ''}
                subtitle={product?.subtitle || ''}
                nutritionLabel={sku?.nutrition_label}
                onClose={() => setIsNutritionLabelOpen(false)}
                hasIngredients={sku?.ingredients?.length > 0}
              />
            )}
          </div>
        )}

        <div className={styles.recipe_description}>
          {currentLTORecipe === 0
            ? (
              <div className={styles.subtext}>
                <FormattedText
                  text={ltoItem?.ltoDescription ?? []}
                />
              </div>
            ) : (
              <span>
                <span className={styles.item_number}>
                  ITEM
                  {' '}
                  {itemNumber}
                  /
                  {totalNumberOfRecipes}
                  {' '}
                  <span style={{ fontSize: '24px' }}>•</span>
                  {' '}
                </span>
                <strong>{title}</strong>
              </span>
            )}
          <div className={styles.subtext}><FormattedText text={description || []} /></div>
          {currentLTORecipe === 1 && (
            <div className={styles.impossible_logo} />
          )}
          {currentLTORecipe === 0 && (
            <span>
              <span>
                <strong>What you get: </strong>
                {' '}
              </span>
              <span>
                <FormattedText
                  text={ltoItem?.whatYouWillGet ?? []}
                />
              </span>
            </span>
          )}

          {tip && (
            <span>
              <span>
                <strong>Robin's Tip: </strong>
                {' '}
              </span>
              <span>
                <FormattedText
                  text={tip ?? []}
                />
              </span>
            </span>
          )}

          <div className={styles.recipe_tags}>
            {['High Protein', 'High Fiber'].map((tag) => (
              <div className={styles.recipe_tag_pill} key={tag}>
                {tag}
              </div>
            ))}
          </div>
        </div>
      </div>
      {(!isNotMobileWidth) && (
        <div className={styles.sticky_footer}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          >
            {!!ltoMenuItem && (
              isLtoAvailable() ? (
                cartActionButton
              ) : (
                <Button
                  text="Sold Out"
                  onClick={() => console.log('Sold Out')}
                  isEnabled={false}
                />
              )
            )}
            <div className={styles.delivery_date}>
              {isLoggedIn ? `${menuSelectorDate} Delivery`
                : `${getFormattedDeliveryDateRangeText(menu?.deliveryOn || '', false)} Delivery`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LimitedTimeOfferingCurrentRecipeInfo;
